/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { convertDateFormatToIntlOptions as t, formatDate as e, formatTimestamp as r, formatTimeOnly as i, formatDateOnly as n } from "../../intl/date.js";
import { isDateField as s, isDateOnlyField as o, isTimestampOffsetField as a } from "../../layers/support/fieldUtils.js";
import { loadArcade as c } from "../../support/arcadeOnDemand.js";
import { getWellKnownBasemapId as l } from "../../support/basemapUtils.js";
import { getTimeZoneFormattingOptions as g, utc as m } from "../../time/timeZoneUtils.js";
const d = ",",
  u = {
    light: ["streets", "gray", "topo", "terrain", "oceans", "osm", "gray-vector", "streets-vector", "topo-vector", "streets-relief-vector", "streets-navigation-vector", "topo-3d", "navigation-3d", "arcgis-light-gray", "arcgis-navigation", "arcgis-streets", "arcgis-streets-relief", "arcgis-topographic", "arcgis-oceans", "osm-standard", "osm-standard-relief", "osm-streets", "osm-streets-relief", "osm-light-gray", "arcgis-terrain", "arcgis-charted-territory", "arcgis-community", "arcgis-colored-pencil", "arcgis-modern-antique", "arcgis-midcentury", "arcgis-newspaper", "arcgis-hillshade-light"],
    dark: ["satellite", "hybrid", "dark-gray", "dark-gray-vector", "streets-night-vector", "navigation-dark-3d", "arcgis-imagery", "arcgis-imagery-standard", "arcgis-dark-gray", "arcgis-navigation-night", "arcgis-streets-night", "osm-dark-gray", "arcgis-nova", "arcgis-hillshade-dark"]
  },
  f = "percent-of-total",
  p = "field",
  y = {
    years: 365,
    months: 30,
    days: 1,
    hours: 1 / 24,
    minutes: 1 / 1440,
    seconds: 1 / 86400,
    milliseconds: 1 / 864e5
  },
  h = new Set(["integer", "small-integer"]);
let v = null;
const T = ["date", "date-only", "timestamp-offset"];
function Z(t) {
  return s(t) || o(t) || a(t);
}
async function $(t) {
  const {
    field: e,
    field2: r,
    field3: i,
    normalizationField: n,
    valueExpression: s
  } = t;
  let o = [];
  if (s) {
    if (!v) {
      const {
        arcadeUtils: t
      } = await c();
      v = t;
    }
    o = v.extractFieldNames(s);
  }
  return e && o.push(e), r && o.push(r), i && o.push(i), n && o.push(n), o;
}
function w(t) {
  let e = t.normalizationType;
  return e || (t.normalizationField ? e = p : null != t.normalizationTotal && (e = f)), e ?? void 0;
}
function U(t) {
  return String(t).padStart(2, "0");
}
function k(t, e, r) {
  let i;
  if ("date" === e || "number" === e) {
    "number" === e && (t = new Date(t));
    i = `TIMESTAMP'${r ? t.getFullYear() : t.getUTCFullYear()}-${U((r ? t.getMonth() : t.getUTCMonth()) + 1)}-${U(r ? t.getDate() : t.getUTCDate())} ${U(r ? t.getHours() : t.getUTCHours())}:${U(r ? t.getMinutes() : t.getUTCMinutes())}:${U(r ? t.getSeconds() : t.getUTCSeconds())}'`;
  } else i = t;
  return i;
}
function D(t, e, r, i) {
  const {
      hasQueryEngine: n
    } = t,
    s = `(${k(r, S(t, r), n)} - ${k(e, S(t, e), n)})`;
  let o = y[i],
    a = "/";
  o < 1 && (o = 1 / o, a = "*");
  return {
    sqlExpression: 1 === o ? s : `(${s} ${a} ${o})`,
    sqlWhere: null
  };
}
function S(t, e) {
  if (e instanceof Date) return "date";
  if ("number" == typeof e) return "number";
  if ("string" == typeof e) {
    const r = t.getField(e);
    if ("<now>" === e.toLowerCase()) return;
    if (s(r)) return "field";
  }
}
function C(t, e = u) {
  for (const r in e) if (e[r].includes(t)) return r;
}
function F(t, e, r = !0) {
  let i = null;
  return t && ("string" == typeof t ? e.includes(t) && (i = t) : i = l(t)), r ? i || "gray" : i;
}
function b(t, e) {
  const r = e && t.getField(e);
  return !!r && h.has(r.type);
}
function M(t) {
  return `cast(${t} as float)`;
}
function N(s, o) {
  const {
    format: a,
    timeZoneOptions: c,
    fieldType: l
  } = o ?? {};
  let d, u;
  if (c && ({
    timeZone: d,
    timeZoneName: u
  } = g(c.layerTimeZone, c.datesInUnknownTimezone, c.viewTimeZone, t(a || "short-date-short-time"), l)), "string" == typeof s && isNaN(Date.parse("time-only" === l ? `1970-01-01T${s}Z` : s))) return s;
  switch (l) {
    case "date-only":
      {
        const r = t(a || "short-date");
        return "string" == typeof s ? n(s, {
          ...r
        }) : e(s, {
          ...r,
          timeZone: m
        });
      }
    case "time-only":
      {
        const r = t(a || "short-time");
        return "string" == typeof s ? i(s, r) : e(s, {
          ...r,
          timeZone: m
        });
      }
    case "timestamp-offset":
      {
        if (!d && "string" == typeof s && new Date(s).toISOString() !== s) return s;
        const i = a || c ? t(a || "short-date-short-time") : void 0,
          n = i ? {
            ...i,
            timeZone: d,
            timeZoneName: u
          } : void 0;
        return "string" == typeof s ? r(s, n) : e(s, n);
      }
    default:
      {
        const r = a || c ? t(a || "short-date-short-time") : void 0;
        return e("string" == typeof s ? new Date(s) : s, r ? {
          ...r,
          timeZone: d,
          timeZoneName: u
        } : void 0);
      }
  }
}
export { M as castIntegerFieldToFloat, T as dateTypes, u as defaultBasemapGroups, d as fieldDelimiter, N as formatAnyDate, C as getBasemapGroup, F as getBasemapId, D as getDateDiffSQL, S as getDateType, $ as getFieldsList, w as getNormalizationType, Z as isAnyDateField, b as isIntegerField, y as unitValueInDays };